import { Box, Button } from "@mui/material";
import {
  buildCDNURL,
  formatPageUrl,
  getSelectedBrandName,
  getSubDomain,
  handleHtmlTags,
} from "@platformx/utilities";
import { useTranslation } from "react-i18next";
import "./Description/Description.css";

// Content types that should render inline instead of as a card
const INLINE_RENDER_TYPES = ["table", "tables"];

// The card's image box is 16:9 (.descAsset), so the CDN crops to that ratio itself. gravity only
// applies when both width and height are set; face keeps people in frame instead of the centre.
const CARD_IMAGE_OPTIONS = "width=1280,height=720,quality=85,format=webp,fit=cover,gravity=face";

// Helper to parse table data from content (only for table content types)
const getTableData = (content: any): { tableData: any[]; mergeInfo: any[]; freezeInfo: any } => {
  try {
    // Try excel_upload field first (common for schema-based table content)
    if (content?.excel_upload) {
      const parsed =
        typeof content.excel_upload === "string"
          ? JSON.parse(content.excel_upload)
          : content.excel_upload;
      return {
        tableData: parsed?.tableData || [],
        mergeInfo: parsed?.mergeInfo || [],
        freezeInfo: parsed?.freezeInfo || {},
      };
    }
    // Try direct tableData field
    if (content?.tableData) {
      return {
        tableData: content.tableData,
        mergeInfo: content?.mergeInfo || [],
        freezeInfo: content?.freezeInfo || {},
      };
    }
    // Try data field (some content types store data here)
    if (content?.data?.tableData) {
      return {
        tableData: content.data.tableData,
        mergeInfo: content.data?.mergeInfo || [],
        freezeInfo: content.data?.freezeInfo || {},
      };
    }
  } catch (e) {
    console.error("Failed to parse table data:", e);
  }
  return { tableData: [], mergeInfo: [], freezeInfo: {} };
};

// Inline table renderer component
const InlineTableRenderer = ({ content }: { content: any }) => {
  const { tableData, freezeInfo } = getTableData(content);

  if (!tableData || tableData.length === 0) {
    return (
      <div style={{ padding: "16px", textAlign: "center", color: "#666" }}>
        <p>No table data available</p>
      </div>
    );
  }

  return (
    <div className='inline-table-wrapper' style={{ margin: "16px 0", overflowX: "auto" }}>
      <table
        className='editor-table'
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #ddd",
        }}>
        <thead>
          <tr className='editor-table-row'>
            {Object.values(tableData[0] || {}).map((header, index) => (
              <th
                key={index}
                className='editor-table-cell'
                style={{
                  border: "1px solid #ddd",
                  padding: "8px 12px",
                  backgroundColor: "#f4f4f4",
                  fontWeight: "normal",
                  textAlign: "left",
                  ...(freezeInfo?.row === 1 || (freezeInfo?.col === 1 && index === 0)
                    ? { position: "sticky", left: 0, zIndex: 1 }
                    : {}),
                }}>
                {String(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.slice(1).map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className='editor-table-row'
              style={{ backgroundColor: rowIndex % 2 === 1 ? "#f9f9f9" : "transparent" }}>
              {Object.values(row).map((value, cellIndex) => (
                <td
                  key={cellIndex}
                  className='editor-table-cell'
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px 12px",
                    textAlign: "left",
                    minWidth: "100px",
                    verticalAlign: "top",
                    ...(freezeInfo?.col === 1 && cellIndex === 0
                      ? { position: "sticky", left: 0, backgroundColor: "#fff", zIndex: 1 }
                      : {}),
                  }}>
                  {String(value ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DescriptionContentCard = ({ content }: { content: any }) => {
  const { i18n } = useTranslation();

  // Check if this content type should render inline as a table
  const contentTypeLower = content?.ContentType?.toLowerCase() || "";
  if (INLINE_RENDER_TYPES.includes(contentTypeLower) && content?.excel_upload?.tableData) {
    return (
      <div className='description-content-card-wrapper'>
        {/* Hidden metadata for re-importing */}
        <div style={{ display: "none" }}>
          <span data-content-type={content.ContentType || "table"} />
          <span data-content={JSON.stringify(content)} />
        </div>
        <InlineTableRenderer content={content} />
      </div>
    );
  }

  const imageURL = buildCDNURL(
    content?.Thumbnail?.Url,
    content?.Thumbnail?.ext,
    "",
    "",
    CARD_IMAGE_OPTIONS,
  );

  const handleViewContent = () => {
    const contentType =
      content.ContentType.toLowerCase() === "vod" ? "video" : content.ContentType.toLowerCase();
    // Slug-first, doc-path fallback — matches getContentUrlPath's authoring-side
    // URL convention (KIWI-5864). content.slug comes from the same content-picker
    // search result already used elsewhere for embedded content (AddContent/AddCollection).
    const pathSegment = content.slug || content.Slug || content.CurrentPageURL;
    const selectedBrandRoute = formatPageUrl(getSelectedBrandName() || i18n.language);
    return `${getSubDomain()}/${selectedBrandRoute}/${contentType}/${pathSegment}`;
  };

  return (
    <div className='description-content-card-wrapper'>
      {/* Hidden metadata for re-importing */}
      <div style={{ display: "none" }}>
        <span data-content-type={content.ContentType || "Article"} />
        <span data-content={JSON.stringify(content)} />
      </div>

      {/* Your actual content card rendering */}
      <div className='content-card'>
        <Box>
          <div style={{ display: "flex", justifyContent: "center", margin: "10px 0px" }}>
            <div style={{ position: "relative", margin: "0px" }} className='content descAsset'>
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: "100%",
                }}>
                {content.Thumbnail.Url !== "" ? (
                  <img
                    src={imageURL}
                    style={{ width: "100%", objectFit: "cover", height: "100%" }}
                    alt={content?.Thumbnail?.AltText ? content?.Thumbnail?.AltText : ""}
                  />
                ) : (
                  <div
                    style={{
                      backgroundColor: content?.background_content?.Color,
                      width: "100%",
                      objectFit: "cover",
                      height: "100%",
                    }}></div>
                )}
              </div>
              <div
                style={{
                  position: "relative",
                  bottom: 0,
                  left: 0,
                  padding: "2% 3%",
                  backgroundImage: "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, #000 100%)",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "end",
                  minHeight: "224px",
                }}>
                <div>
                  <h1
                    style={{
                      fontSize: "28px",
                      fontWeight: "bold",
                      color: "#fff",
                      textTransform: "uppercase",
                      WebkitLineClamp: 1,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      wordWrap: "break-word",
                      margin: "0",
                    }}>
                    {content.Title}
                  </h1>
                  <h1
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#fff !important",
                      WebkitLineClamp: 2,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      wordWrap: "break-word",
                    }}>
                    {handleHtmlTags(content.Description)}
                  </h1>
                  <a
                    href={handleViewContent()}
                    target='_blank'
                    style={{
                      textDecoration: "none",
                      width: "140px",
                      height: "40px",
                    }}
                    rel='noreferrer'>
                    <Button
                      style={{
                        padding: "0 8px",
                        borderRadius: "4px",
                        backgroundColor: "#fff",
                        width: "140px",
                        height: "40px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}>
                      {content.ContentType === "Quiz" || content.ContentType === "Poll"
                        ? "Start"
                        : "View"}{" "}
                      {content.ContentType}
                      {/* <img src={BigArrow} style={{ marginLeft: "10px" }} /> */}
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <br />
        </Box>
      </div>
    </div>
  );
};

export default DescriptionContentCard;
